import * as Sentry from '@sentry/react';
import { User } from 'core/entities';
import { filterBreadcrumbs } from 'core/exceptions/exception-handler/utils';
import { getDeviceType } from 'utils/get-user-agent-data/utils';
import type { Breadcrumb, BreadcrumbHint, Integration } from '@sentry/core';

export type InitExceptionHandlerProps = {
    release: string;
    dsn: string;
    integrations?: Integration[];
    env?: string;
};

type ExceptionHandlerUserProps = {
    accountId?: string;
    isAutomation?: boolean;
    user: User;
};

type ExceptionLike = Sentry.Exception | undefined;
type ErrorEventLike = Sentry.ErrorEvent;

export const initExceptionHandler = (props: InitExceptionHandlerProps) => {
    const sentryInitProps = getSentryInitProps(props);
    Sentry.init(sentryInitProps);
    Sentry.setTag('platform', getDeviceType());
};

export const setExceptionHandlerUser = ({
    accountId,
    isAutomation,
    user,
}: ExceptionHandlerUserProps) => {
    Sentry.setUser({ id: user.id });
    Sentry.setTag('accountId', accountId);
    isAutomation && Sentry.setTag('isAutomation', true);

    if (!user.featureFlags.enable_sentry_performance_monitor_ff) {
        Sentry.getClient()!.getOptions().integrations = Sentry.getClient()!
            .getOptions()
            .integrations.filter(
                (i) => i.name !== 'BrowserTracing' && i.name !== 'BrowserProfiling',
            );
    }
};

export const getSentryInitProps = (props: InitExceptionHandlerProps) => {
    const { release, dsn, env, integrations } = props;
    const isDevelopEnv = env === 'development';
    const defaultInitProps: Sentry.BrowserOptions = {
        release,
        dsn,
        enabled: !isDevelopEnv,
        environment: env,
        ignoreErrors: [/^ResizeObserver loop/, /(^UnhandledRejection).*value: Timeout/],
        allowUrls: [/placer\.team/, /placer\.ai/],
        tracesSampleRate: 0.1,
        integrations: generateSentryIntegrations(integrations),
        beforeBreadcrumb(breadcrumb: Breadcrumb, hint?: BreadcrumbHint) {
            return breadcrumb?.data?.status_code !== 200
                ? filterBreadcrumbs(breadcrumb)
                : breadcrumb;
        },
        beforeSend: filterErrorEvent,
    };

    return defaultInitProps;
};

export function filterErrorEvent(errorEvent: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    if (isUnactionableErrorEvent(errorEvent)) {
        return null;
    }
    return errorEvent;
}

export function isUnactionableErrorEvent(event: ErrorEventLike): boolean {
    const exception = event.exception?.values?.[0];
    const eventMessage = event.message ?? '';

    return [
        hasUnexceptionalExceptionType(exception),
        matchesTypeValuePatterns(exception),
        hasNetworkErrorMessage(eventMessage),
    ].some((check) => check === true);
}

export function hasUnexceptionalExceptionType(exception: ExceptionLike): boolean {
    const unactionableTypes = [
        'aborterror',
        'abort_error',
        'networkerror',
        'timeouterror',
        'domexception',
        'chunkloaderror',
    ];

    return unactionableTypes.includes(exception?.type?.toLowerCase() ?? '');
}

export function matchesTypeValuePatterns(exception: ExceptionLike): boolean {
    const patterns = [
        { type: 'typeerror', value: 'failed to fetch' },
        { type: 'typeerror', value: 'cors' },
        { type: 'domexception', value: 'aborted' },
        { type: 'error', value: 'loading chunk' },
        { type: 'error', value: 'abort_error' },
        { type: 'error', value: 'fetch error' },
        { type: 'error', value: 'abort' },
    ];

    return patterns.some(({ type, value }) => {
        const exceptionType = exception?.type?.toLowerCase() ?? '';
        const exceptionValue = exception?.value?.toLowerCase() ?? '';
        return exceptionType === type && exceptionValue.includes(value);
    });
}

export function hasNetworkErrorMessage(message?: string): boolean {
    const networkMessages = [
        'offline',
        'timeout',
        'abort',
        'connection reset',
        'network request failed',
        'socket hang up',
        'econnaborted',
        'etimedout',
        'network error: failed to fetch',
        'failed to fetch',
        'network error',
    ];

    return networkMessages.some((networkMessage) =>
        message?.toLowerCase().includes(networkMessage),
    );
}

export function generateSentryIntegrations(externalIntegrations?: Integration[]): Integration[] {
    return (
        externalIntegrations ?? [
            Sentry.browserTracingIntegration(),
            Sentry.browserProfilingIntegration(),
            Sentry.captureConsoleIntegration({ levels: ['error'] }),
        ]
    );
}
